// src/components/tabs/TabStrip.tsx
// The tab strip of one pane. Every pane has its own, as in JetBrains — with a
// single pane it sits directly under the navbar and reads as part of it.
//
// Tabs and nothing else. The open page's controls used to hang here too, and
// took so much of the row that the tab's own label needed a scroller to survive
// a narrow pane; they live in the navbar now (features/tabs/PaneChrome.tsx).
//
// Dragging uses native HTML5 drag events rather than a drag library: the two
// gestures needed here are reordering within a strip and dropping onto another
// pane, both of which the platform already gives us.

import { useCallback, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import { TAB_DRAG_TYPE } from '@/features/tabs/logic';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { useNarrowViewport } from '@/hooks/useNarrowViewport';
import type {
  ContentAlign,
  ContentWidth,
  TabGroupNode,
  WorkspaceTab,
} from '@/features/tabs/types';

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function PinIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 17v5M9 3h6l-1 8h3l-5 6-5-6h3z" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" stroke="none">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function SplitIcon({ direction }: { direction: 'row' | 'column' }) {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      {direction === 'row' ? <path d="M12 3v18" /> : <path d="M3 12h18" />}
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/** Small picture of where the text sits, so the choice reads before it is made. */
function AlignIcon({ align }: { align: ContentAlign }) {
  const bars =
    align === 'left'
      ? [{ x: 4, w: 10 }, { x: 4, w: 14 }, { x: 4, w: 8 }]
      : align === 'right'
        ? [{ x: 10, w: 10 }, { x: 6, w: 14 }, { x: 12, w: 8 }]
        : [{ x: 7, w: 10 }, { x: 5, w: 14 }, { x: 8, w: 8 }];
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      {bars.map((bar, i) => (
        <line key={bar.x + i} x1={bar.x} x2={bar.x + bar.w} y1={7 + i * 5} y2={7 + i * 5} />
      ))}
    </svg>
  );
}

const WIDTHS: { value: ContentWidth; key: string }[] = [
  { value: 'full', key: 'layout.tabs.width.full' },
  { value: 'contained', key: 'layout.tabs.width.contained' },
];

const ALIGNS: { value: ContentAlign; key: string }[] = [
  { value: 'left', key: 'layout.tabs.align.left' },
  { value: 'center', key: 'layout.tabs.align.center' },
  { value: 'right', key: 'layout.tabs.align.right' },
];

function MenuLabel({ children }: { children: string }) {
  return (
    <div className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">{children}</div>
  );
}

function MenuItem({
  label,
  icon,
  checked,
  disabled,
  onSelect,
}: {
  label: string;
  icon?: React.ReactNode;
  checked?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={checked}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm ${
        disabled
          ? 'cursor-not-allowed text-muted-foreground/50'
          : `hover:bg-accent ${checked ? 'font-medium text-foreground' : 'text-foreground'}`
      }`}
    >
      <span className="w-3.5 shrink-0">{checked && <CheckIcon />}</span>
      {icon}
      {label}
    </button>
  );
}

/**
 * Everything you can do to a tab and to the pane holding it, in one menu.
 *
 * Pinning, closing, splitting and how the pane presents its content are all
 * answers to "what about this tab", and splitting them across two popovers is
 * how the alignment control got lost the first time. The strip used to offer a
 * second door onto this same menu from its right edge; every tab already
 * carries one, so that was a button spending room on nothing.
 */
const TabMenu = observer(({
  group,
  tab,
  anchor,
  onClose,
}: {
  group: TabGroupNode;
  tab: WorkspaceTab;
  /** Rect of the trigger, captured when the menu opened. */
  anchor: DOMRect;
  onClose: () => void;
}) => {
  const { t } = useI18n();
  const narrow = useNarrowViewport();
  const alignable = group.width === 'contained';
  const pick = (run: () => void) => () => {
    run();
    onClose();
  };

  // Positioning, the body portal and dismissal all come from the app's popover
  // core rather than from here. This menu had its own copy of each — it had to,
  // to escape the 28px scrolling strip that was clipping it — but keeping a
  // second implementation once one exists is how three of them appeared in the
  // first place.
  return (
    <AnchoredMenu
      anchor={anchor}
      open
      onClose={onClose}
      align="start"
      width={224}
      ariaLabel={t('layout.tabs.menu.open')}
    >
      <MenuLabel>{t('layout.tabs.menu.tabGroup')}</MenuLabel>
      <MenuItem
        label={tab.pinned ? t('layout.tabs.menu.unpin') : t('layout.tabs.menu.pin')}
        icon={<PinIcon filled={tab.pinned} />}
        checked={tab.pinned}
        onSelect={pick(() => tabsStore.togglePinned(tab.id))}
      />
      <MenuItem label={t('layout.tabs.menu.close')} onSelect={pick(() => tabsStore.close(tab.id))} />
      <MenuItem
        label={t('layout.tabs.menu.closeOthers')}
        onSelect={pick(() => tabsStore.closeOthers(group.id, tab.id))}
      />
      <MenuItem
        label={t('layout.tabs.menu.closeRight')}
        onSelect={pick(() => tabsStore.closeToTheRight(group.id, tab.id))}
      />

      {/* Splitting is not offered where only one pane is rendered: the second
          pane would exist in the store and nowhere on screen. */}
      {!narrow && (
        <>
          <div className="my-1 border-t border-border" />
          <MenuLabel>{t('layout.tabs.menu.splitGroup')}</MenuLabel>
          <MenuItem
            label={t('layout.tabs.menu.splitRight')}
            icon={<SplitIcon direction="row" />}
            onSelect={pick(() => tabsStore.splitWithTab(group.id, tab.id, 'row'))}
          />
          <MenuItem
            label={t('layout.tabs.menu.splitDown')}
            icon={<SplitIcon direction="column" />}
            onSelect={pick(() => tabsStore.splitWithTab(group.id, tab.id, 'column'))}
          />
        </>
      )}

      <div className="my-1 border-t border-border" />
      <MenuLabel>{t('layout.tabs.widthLabel')}</MenuLabel>
      {WIDTHS.map((w) => (
        <MenuItem
          key={w.value}
          label={t(w.key)}
          checked={group.width === w.value}
          onSelect={() => tabsStore.setPresentation(group.id, { width: w.value })}
        />
      ))}

      <MenuLabel>{t('layout.tabs.alignLabel')}</MenuLabel>
      {ALIGNS.map((a) => (
        <MenuItem
          key={a.value}
          label={t(a.key)}
          icon={<AlignIcon align={a.value} />}
          checked={alignable && group.align === a.value}
          disabled={!alignable}
          onSelect={() => tabsStore.setPresentation(group.id, { align: a.value })}
        />
      ))}
      {!alignable && (
        <p className="px-2 pb-1 pt-0.5 text-xs text-muted-foreground">
          {t('layout.tabs.alignNeedsContained')}
        </p>
      )}
    </AnchoredMenu>
  );
});

export const TabStrip = observer(({ group }: { group: TabGroupNode }) => {
  const { t } = useI18n();
  const tabs = tabsStore.tabsOf(group.id);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  /** Which tab's menu is open, and where the trigger that opened it sits. */
  const [menu, setMenu] = useState<{ tabId: string; anchor: DOMRect } | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  if (tabs.length === 0) return null;

  const isFocusedPane = tabsStore.focusedGroupId === group.id;

  return (
    // Drop handling belongs to the whole strip, including the empty space after
    // the last tab — that is where a tab dragged from another pane naturally
    // lands. The tablist role sits on the inner row instead, because an element
    // announcing itself as a tablist has to be focusable once it also takes
    // input, and the strip is not the thing you focus; its tabs are.
    <div
      ref={stripRef}
      className={`relative flex h-9 items-center gap-0.5 border-b bg-background px-1 ${
        isFocusedPane ? 'border-border' : 'border-border/50'
      }`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(TAB_DRAG_TYPE)) e.preventDefault();
      }}
      onDrop={(e) => {
        const tabId = e.dataTransfer.getData(TAB_DRAG_TYPE);
        if (tabId) tabsStore.reorderOrMove(tabId, group.id, dropIndex ?? tabs.length);
        setDropIndex(null);
      }}
    >
      <div
        role="tablist"
        aria-orientation="horizontal"
        className="no-scrollbar flex min-w-0 items-center gap-0.5 overflow-x-auto"
        onWheel={(e) => {
          // A strip is one row: a wheel that would scroll a page vertically
          // should move along the tabs instead. Only claimed when there is
          // something to scroll, so the page still scrolls over a short strip.
          const el = e.currentTarget;
          if (el.scrollWidth <= el.clientWidth) return;
          e.preventDefault();
          el.scrollLeft += e.deltaY !== 0 ? e.deltaY : e.deltaX;
        }}
      >
        {tabs.map((tab, index) => {
          const isActive = group.activeTabId === tab.id;
          return (
            <div
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(TAB_DRAG_TYPE, tab.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(TAB_DRAG_TYPE)) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                setDropIndex(e.clientX < box.left + box.width / 2 ? index : index + 1);
              }}
              onClick={() => tabsStore.activateTab(tab.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  tabsStore.activateTab(tab.id);
                  return;
                }
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const step = e.key === 'ArrowRight' ? 1 : -1;
                  const next = tabs[(index + step + tabs.length) % tabs.length];
                  if (next) tabsStore.activateTab(next.id);
                  return;
                }
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  tabsStore.close(tab.id);
                }
              }}
              onAuxClick={(e) => {
                // Middle click closes, as everywhere else tabs exist.
                if (e.button === 1) {
                  e.preventDefault();
                  tabsStore.close(tab.id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ tabId: tab.id, anchor: e.currentTarget.getBoundingClientRect() });
              }}
              className={`group relative flex h-7 cursor-default select-none items-center gap-1 whitespace-nowrap rounded-t px-2 text-sm ${
                isActive
                  ? 'bg-accent text-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
              } ${dropIndex === index ? 'ml-2' : ''}`}
              title={tab.path}
            >
              {tab.pinned && <PinIcon filled />}
              <span className="max-w-[12rem] truncate">{tab.title}</span>

              {/* The menu has a button of its own, because a right-click is not
                  a thing anyone finds by looking. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const anchor = e.currentTarget.getBoundingClientRect();
                  setMenu((prev) => (prev?.tabId === tab.id ? null : { tabId: tab.id, anchor }));
                }}
                className={`rounded p-0.5 hover:bg-background focus:opacity-100 ${
                  menu?.tabId === tab.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
                aria-label={t('layout.tabs.menu.open')}
                aria-haspopup="menu"
                aria-expanded={menu?.tabId === tab.id}
              >
                <MoreIcon />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  tabsStore.close(tab.id);
                }}
                className="rounded p-0.5 opacity-0 hover:bg-background focus:opacity-100 group-hover:opacity-100"
                aria-label={t('layout.tabs.close')}
              >
                <CloseIcon />
              </button>

              {menu?.tabId === tab.id && (
                <TabMenu group={group} tab={tab} anchor={menu.anchor} onClose={closeMenu} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
